import { FC } from 'react';

import {
  RESALE_PENDING_WAIT_MINUTES,
  ResaleEmptyStateType,
  resaleEmptyStateImage
} from '../../constants/resale-empty-state';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import * as Styles from './styles';

type ResaleEmptyStateProps = {
  isActive?: boolean;
  isSold?: boolean;
  isDeactivated?: boolean;
  status?: string;
  errorType?: 'notFound' | 'generic' | 'ownResale' | null;
  titleOverride?: string;
  subtitleOverride?: string;
};

const I18N_KEY_MAP : Record<ResaleEmptyStateType, { title : string; subtitle : string }> = {
  pending : { title : 'PENDING_TITLE', subtitle : 'PENDING_SUBTITLE' },
  sold : { title : 'SOLD_TITLE', subtitle : 'SOLD_SUBTITLE' },
  notFound : { title : 'NOT_FOUND_TITLE', subtitle : 'NOT_FOUND_SUBTITLE' },
  ownResale : { title : 'OWN_RESALE_TITLE', subtitle : 'OWN_RESALE_SUBTITLE' },
  default : { title : 'DEFAULT_TITLE', subtitle : 'DEFAULT_SUBTITLE' },
};

/**
 * Empty state component for resale pages
 * Shows contextual messages based on resale state:
 * - Not Found: Resale not found (404 error)
 * - Pending: Someone is currently buying (isActive: false, isSold: false, status: 'pending_payment')
 * - Sold: Already purchased (isActive: true, isSold: true, status: 'sold')
 * - Default: Deactivated or other unavailable states
 */
const ResaleEmptyState: FC<ResaleEmptyStateProps> = ({
  isActive = false,
  isSold = false,
  status,
  errorType = null,
  titleOverride,
  subtitleOverride,
}) => {
  const { translate } = useI18nContext();

  // Priority: notFound > ownResale > sold > pending > default
  const getMessageType = (): ResaleEmptyStateType => {
    if (errorType === 'notFound') {
      return 'notFound';
    }
    if (errorType === 'ownResale') {
      return 'ownResale';
    }
    if (isActive && isSold && status === 'sold') {
      return 'sold';
    }
    if (!isActive && !isSold && status === 'pending_payment') {
      return 'pending';
    }
    return 'default';
  };

  const messageType = getMessageType();
  const { title : titleKey, subtitle : subtitleKey } = I18N_KEY_MAP[messageType];

  return (
    <Styles.Container id="resale-empty-state">
      <Styles.Image
        src={resaleEmptyStateImage}
        alt="Resale not available"
        id="resale-empty-state-image"
      />
      <Styles.Title id="resale-empty-state-title">
        {titleOverride ?? translate(['RESALE', 'EMPTY_STATE', titleKey])}
      </Styles.Title>
      <Styles.Subtitle id="resale-empty-state-subtitle">
        {subtitleOverride ?? translate(
          ['RESALE', 'EMPTY_STATE', subtitleKey],
          { minutes : RESALE_PENDING_WAIT_MINUTES },
        )}
      </Styles.Subtitle>
    </Styles.Container>
  );
};

export default ResaleEmptyState;
